import React from 'react';
import styled from 'styled-components';
import { Theme } from '@/styles/theme';

interface ContainerProps {
  $rounded?: keyof Theme['rounded'];
  $disabled?: boolean;
}

interface SliderFillProps extends ContainerProps {
  $fill: string;
  $isBothFill?: boolean;
}

const InnerSlider = styled.input<ContainerProps>`
  position: absolute;
  top: calc(50% - 4px);
  width: 100%;
  height: 12px;
  -webkit-appearance: none;
  background: transparent;
  margin: 0;
  outline: none;

  &:active {
    cursor: grabbing;
  }

  &:focus:not(&:active) {
    cursor: pointer;
  }
  ${(p) => p.theme.rounded[p.$rounded ?? 'md']};

  &:disabled {
    cursor: default;
  }
`;

const SliderRail = styled.div<ContainerProps>`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  position: absolute;
  top: calc(50% - 2px);
  ${(p) => p.theme.rounded[p.$rounded ?? 'md']};
`;

const SliderFillTrack = styled.div<SliderFillProps>`
  width: ${(props) => props.$fill};
  height: 8px;
  background: ${(p) =>
    p.$disabled
      ? 'rgba(255, 255, 255, 0.2)'
      : 'linear-gradient(90deg, #ff474c 0%, #3861d1 100%)'};
  position: absolute;
  top: calc(50% - 2px);
  ${(p) => p.theme.rounded[p.$rounded ?? 'md']};
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
`;

const SliderNonFillTrack = styled.div<SliderFillProps>`
  width: ${(props) => props.$fill};
  height: 8px;
  background: ${(p) =>
    p.$disabled
      ? 'rgba(255, 255, 255, 0.2)'
      : 'linear-gradient(90deg, #3861d1 0%, #000 100%)'};
  position: absolute;
  right: 0;
  top: calc(50% - 2px);

  ${(p) => p.theme.rounded[p.$rounded ?? 'md']};
  border-bottom-left-radius: 0;
  border-top-left-radius: 0;
`;

const SliderContainer = styled.div`
  width: 100%;
  height: 16px;
  position: relative;

  ::-webkit-slider-thumb {
    -webkit-appearance: none;
    background-color: rgba(248, 248, 248, 0.4);
    backdrop-filter: blur(4px);
    width: 16px;
    height: 16px;
    border-radius: 8px;
    border: 1px solid rgba(248, 248, 248, 0.5);
  }
`;

interface Props
  extends ContainerProps,
    React.ComponentPropsWithoutRef<'input'> {
  children?: React.ReactNode;
  isBothFill?: boolean;
}

export default function Slider({ children, isBothFill, ...props }: Props) {
  return (
    <SliderContainer>
      <SliderRail />
      <SliderFillTrack $disabled={props.disabled} $fill={`${props.value}%`} />
      <SliderNonFillTrack
        $disabled={props.disabled}
        $fill={
          isBothFill ? `${100 - parseFloat(props.value as string)}%` : '0%'
        }
      />
      <InnerSlider
        {...props}
        type="range"
        disabled={props.disabled}
        value={props.disabled ? 0 : props.value}
        min={0}
        max={100}
        onChange={props.onChange}
      />
    </SliderContainer>
  );
}
